import { useMemo, useState } from 'react'

import { useFormContext, Validate } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { getCustomDateFormat } from '@cash-design-system/react'
import { FormBlockerElementDateInputElement } from '@cash-web/protos/squareup/franklin/form_blocker.pb'
import { useArcadeMigrationFeatureFlag } from '@cash-web/shared-data-access-arcade-migration'
import { regionConfig } from '@cash-web/shared-region-feature-main'
import { Region, useCountryFromRegionProvider } from '@cash-web/shared-region-ui-context-main'
import { isValidFormattedDate } from '@cash-web/shared-util-validation'

import { DatePickerInput, DatePickerInputProps } from '../DatePickerInput/DatePickerInput'
import { FormInput } from '../FormInput'

export type Validation<T> = Record<string, Validate<T>>

// eslint-disable-next-line @typescript-eslint/no-explicit-any
interface Props<T = any> extends Omit<FormBlockerElementDateInputElement, 'minimum_date' | 'maximum_date'> {
  formSectionIndex: number
  id?: string
  shouldAutoFocus?: boolean
  handleDateInputChange?: () => void
  picker?: boolean | DatePickerInputProps['picker']
  validation?: Validation<T>
}

type DateObject = {
  day: string
  month: string
  year: string
}

export function getDateFormat(regionCode: Region) {
  const { numericDateFormat: format } = regionConfig[regionCode] ?? { numericDateFormat: 'DD/MM/YYYY' }
  return getCustomDateFormat(format)
}

function formatYear(year: string) {
  if (year.length === 4) {
    return `${year}/`
  }

  return year
}

function formatMonth(month: string) {
  if (month === '0/') return '01/'
  if (/^[1-9]\//.test(month)) return `0${month}`

  if (month.length === 1 && Number(month) > 1) {
    month = `0${Number(month)}`
  } else if (month.length === 2) {
    if (Number(month) === 0) {
      month = '01'
    } else if (Number(month) > 12) {
      month = '12'
    }
  }

  return month.length === 2 ? `${month}/` : month
}

function formatDay(day: string) {
  if (day === '0/') return '01/'
  if (/^[1-9]\//.test(day)) return `0${day}`

  if (day.length === 1 && Number(day) > 3) {
    day = `0${Number(day)}`
  } else if (day.length === 2) {
    if (Number(day) === 0) {
      day = '01'
    } else if (Number(day) > 31) {
      day = '31'
    }
  }

  return day.length === 2 ? `${day}/` : day
}
const removeNonDateValues = (value: string) => value.replace(/[^\d/]/g, '')

const dateInputFormatter = (regionCode: Region) => {
  const { format, parseDate } = getDateFormat(regionCode)

  return (prevValue = '') => {
    return (value = '') => {
      const isBackspace = value.length === prevValue.length - 1
      if (isBackspace) return value

      const invalidSlash = value.endsWith('//')
      if (invalidSlash) return prevValue

      if (value === '/') return ''

      const parsedValue = removeNonDateValues(value)

      const { day, month, year } = parseDate(parsedValue, true)

      let formattedValue = `${format}/`
      formattedValue = formattedValue.replace('DD/', day ? `${formatDay(day)}` : '')
      formattedValue = formattedValue.replace('MM/', month ? `${formatMonth(month)}` : '')
      formattedValue = formattedValue.replace('YYYY/', year ? `${formatYear(year)}` : '')

      const isFullDate = day && month && year

      return isFullDate && formattedValue.endsWith('/') ? formattedValue.slice(0, -1) : formattedValue
    }
  }
}
// This input will accept dates in the format MM/DD/YYYY for US users, and
// DD/MM/YYYY for GB users but will always report the values in an {month, day, year} objects
export function DateInputElement(props: Props) {
  const { t } = useTranslation()
  const regionCode = useCountryFromRegionProvider()
  const { setValue, getValues, trigger } = useFormContext()
  const isArcadeMigrationEnabled = useArcadeMigrationFeatureFlag('apps')

  const dateFormat = regionCode ? getDateFormat(regionCode) : undefined

  const key = `${props.formSectionIndex}.date_input_result`
  const currentValue = getValues(key)

  const getInitialInputValue = () => {
    if (!currentValue) return ''

    if (typeof currentValue === 'string') {
      return currentValue
    }

    if (
      typeof currentValue === 'object' &&
      'day' in currentValue &&
      'month' in currentValue &&
      'year' in currentValue
    ) {
      const { day, month, year } = currentValue
      if (day && month && year) {
        if (dateFormat?.format === 'MM/DD/YYYY') {
          return `${month}/${day}/${year}`
        } else {
          return `${day}/${month}/${year}`
        }
      }
    }

    return ''
  }

  const [inputValue, setInputValue] = useState(getInitialInputValue)

  const requiredErrorMsg = t('Enter a date.', {
    context: 'This is the error message for the full numeric date input when the required value is missing.',
  })

  const format = dateFormat?.format
  const invalidErrorMsg = t(`Enter a valid date ({format}).`, {
    context: 'This is the error message for the full numeric date input when it is not properly formatted.',
    format,
  })

  const localizedDateInputFormatter = useMemo(() => {
    return regionCode ? dateInputFormatter(regionCode) : undefined
  }, [regionCode])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formattedValue = localizedDateInputFormatter ? localizedDateInputFormatter(inputValue)(e.target.value) : ''
    if (!isArcadeMigrationEnabled) {
      setValue(key, formattedValue, { shouldDirty: true })
    }
    setInputValue(formattedValue)
    props.handleDateInputChange?.()
  }

  const addPickerSelectListener = (options: DatePickerInputProps['picker']): typeof options => ({
    ...options,
    onSelect: (...args) => {
      trigger(key)
      options?.onSelect?.(...args)
    },
  })

  const pickerProps = props.picker
    ? {
        customInput: DatePickerInput,
        picker: addPickerSelectListener(props.picker === true ? {} : props.picker),
        defaultValue: currentValue,
      }
    : {}

  if (isArcadeMigrationEnabled && currentValue && typeof currentValue === 'string' && dateFormat) {
    const transformedValue = dateFormat.parseDate(currentValue)
    setValue(key, transformedValue, { shouldDirty: false })
    if (!inputValue) {
      setInputValue(currentValue)
    }
  }

  return (
    <FormInput
      {...pickerProps}
      name={key}
      id={props.id}
      placeholder={format}
      autoFocus={props.shouldAutoFocus}
      aria-label={props.accessibility_text}
      value={inputValue}
      transform={value => {
        if (!value) return undefined
        if (typeof value === 'object' && 'day' in value && 'month' in value && 'year' in value) {
          return value
        }
        return dateFormat?.parseDate(value as string)
      }}
      onChange={handleChange}
      rules={{
        required: { value: !props.is_optional, message: requiredErrorMsg },
        validate: {
          format: date => {
            if (!date && props.is_optional) return true
            // TODO: We should have better validation. We aren't doing anything with minimum or maximum dates yet.
            //       Also, reusing `isValidFormattedDate` works but is a little clunky.
            return isValidFormattedDate(`${date.month}/${date.day}/${date.year}`) || invalidErrorMsg
          },
          ...props.validation,
        },
      }}
    />
  )
}

function getDateFromDateObject(date: DateObject) {
  return new Date(parseInt(date.year, 10), parseInt(date.month, 10) - 1, parseInt(date.day, 10))
}

export function DateInputElementWithFormBlockerDefaults({
  minimum_date = new Date(1900, 0, 1).toISOString(), // default specified in the proto
  maximum_date = new Date(2099, 11, 31).toISOString(), // default specified in the proto
  ...props
}: Props & { minimum_date?: string; maximum_date?: string; default_value?: string }) {
  const region = useCountryFromRegionProvider() ?? 'US'
  const minDate = new Date(Date.parse(minimum_date))
  const maxDate = new Date(Date.parse(maximum_date))

  const { locale } = regionConfig[region] ?? { locale: 'en-GB' }
  const options: Intl.DateTimeFormatOptions = {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }

  const validation = {
    minimumDate: (date: DateObject) => {
      const enteredDate = getDateFromDateObject(date)
      return enteredDate >= minDate || `Date must be ${minDate.toLocaleDateString(locale, options)} or later.`
    },
    maximumDate: (date: DateObject) => {
      const enteredDate = getDateFromDateObject(date)
      return enteredDate <= maxDate || `Date must be ${maxDate.toLocaleDateString(locale, options)} or earlier.`
    },
    ...props.validation,
  }

  return <DateInputElement {...props} validation={validation} />
}
